import { copyToClipboard } from "app/client/lib/clipboardUtils";
import { handleSubmit } from "app/client/lib/formUtils";
import { inlineMarkdown } from "app/client/lib/markdown";
import { buildURL } from "app/client/lib/urlUtils";
import { reportError } from "app/client/models/errors";
import { getHomeUrl } from "app/client/models/homeUrl";
import { textInput } from "app/client/ui/inputs";
import * as loginCss from "app/client/ui/LoginPagesCss";
import * as oauthCss from "app/client/ui/OAuthCss";
import { buildOAuthClientInfo } from "app/client/ui/OAuthForms";
import { SignInWithGristAPI } from "app/client/ui/SignInWithGristAPI";
import { showTransientTooltip } from "app/client/ui/tooltips";
import { createUserImage } from "app/client/ui/UserImage";
import { cssMemberPrimary, cssMemberSecondary } from "app/client/ui/UserItem";
import { textButton } from "app/client/ui2018/buttons";
import { icon } from "app/client/ui2018/icons";
import { cssLink } from "app/client/ui2018/links";
import { commonUrls } from "app/common/gristUrls";
import { LoginPageConfig } from "app/common/LoginPageConfig";
import { OAuthClientWithSecret } from "app/common/OAuthClientTypes";
import { tokens } from "app/common/ThemePrefs";
import { getGristConfig } from "app/common/urlUtils";
import { ExtendedUser, FullUser, UserAPIImpl } from "app/common/UserAPI";

import { Disposable, dom, makeTestId, Observable, styled } from "grainjs";
import pick from "lodash/pick";

const testId = makeTestId("test-sign-in-with-grist-");

/**
 * The Sign in with getgrist.com register page (`/oauth/register`).
 */
export class RegisterPage extends Disposable {
  private readonly _client = Observable.create<OAuthClientWithSecret | null>(this, null);
  private readonly _user = Observable.create<ExtendedUser | null>(this, null);
  private readonly _signInWithGristAPI = new SignInWithGristAPI();
  private readonly _userAPI = new UserAPIImpl(getHomeUrl());

  private readonly _name: Observable<string>;
  private readonly _uri: Observable<string>;
  private readonly _showLeaveUriUnchangedText: boolean;
  private readonly _pending: Observable<boolean>;

  constructor() {
    super();

    this._name = Observable.create(this, "");

    const defaultUri = new URLSearchParams(window.location.search).get("uri")?.trim() ?? "";
    this._uri = Observable.create(this, defaultUri);

    if (defaultUri) {
      // If the default URI looks like a localhost value, hide text that
      // suggests the user should leave its value unchanged in the form.
      try {
        const { hostname } = new URL(defaultUri);
        const localhostValues = new Set(["localhost", "127.0.0.1", "[::1]"]);
        this._showLeaveUriUnchangedText = !localhostValues.has(hostname);
      } catch {
        // URL must be invalid.
        this._showLeaveUriUnchangedText = false;
      }
    } else {
      this._showLeaveUriUnchangedText = false;
    }

    this._pending = Observable.create(this, false);

    this._fetchUser().catch(reportError);
  }

  public buildDom() {
    return dom.maybe(this._user, user =>
      loginCss.pageContainer(
        loginCss.flexJustifyCenter(
          loginCss.formContainer(
            dom.domComputed(this._client, client =>
              !client ?
                this._buildRegisterClientForm(user) :
                this._buildCopySecretPage(user, client),
            ),
          ),
        ),
      ),
    );
  }

  /**
   * Builds a form to register an OAuth client for getgrist.com authentication.
   */
  private _buildRegisterClientForm(user: FullUser) {
    return [
      loginCss.flexJustifyCenter(loginCss.gristLogo()),
      loginCss.centeredFormHeading("Register your Grist server", testId("heading")),
      loginCss.centeredFormSubheading(
        "to set up ",
        loginCss.lightlyBolded("getgrist.com authentication"),
      ),
      cssForm(
        cssFormField(
          loginCss.formLabel("Owner", { for: "owner" }),
          oauthCss.changeAccountButton(
            { id: "owner", name: "owner" },
            oauthCss.accountSmall(
              createUserImage(user, "small"),
              oauthCss.accountNameAndEmail(cssMemberPrimary(
                dom("span", user.email, testId("change-account-email"))),
              ),
              icon("Pencil"),
            ),
            dom.on("click", () => {
              window.location.assign(buildURL("/oauth/select-account").href);
            }),
            testId("change-account"),
          ),
          cssFormFieldHint("Used as the contact email on the sign-in page."),
        ),
        cssFormField(
          loginCss.formLabel("Server name", { for: "name" }),
          textInput(this._name,
            {
              id: "name",
              name: "name",
              type: "text",
              required: "true",
              // Require at least one non-whitespace character.
              pattern: ".*\\S+.*",
              title: "Name must not be empty",
              placeholder: "ACME Corp",
            },
            testId("name"),
          ),
          cssFormFieldHint("Displayed on the sign-in page. Pick something users will recognize."),
        ),
        cssFormField(
          loginCss.formLabel("Server URL", { for: "uri" }),
          textInput(this._uri,
            {
              id: "uri",
              name: "uri",
              type: "url",
              required: "true",
              placeholder: "https://grist.example.com",
            },
            testId("uri"),
          ),
          cssFormFieldHint(
            "Your server's URL." +
            (this._showLeaveUriUnchangedText ? " In most cases, you shouldn't need to change this." : ""),
            testId("uri-hint"),
          ),
        ),
        cssFormButtons(
          cssBasicButtonLink("Cancel",
            { href: getHomeUrl() },
            testId("cancel"),
          ),
          cssPrimaryButton(
            dom.text(use => !use(this._pending) ? "Register" : "Submitting..."),
            dom.boolAttr("disabled", this._pending),
            {
              type: "submit",
            },
            testId("register"),
          ),
        ),
        loginCss.centeredText(
          cssLink("Learn more about getgrist.com authentication", {
            href: commonUrls.help,
            target: "_blank",
          }),
        ),
        handleSubmit({
          pending: this._pending,
          onSubmit: ({ name, uri }) => this._signInWithGristAPI.registerClient({ name, uri }),
          onSuccess: client => this._client.set(client),
        }),
      ),
    ];
  }

  /**
   * Builds a page to copy a secret to the Grist server to finish setting up
   * getgrist.com authentication.
   */
  private _buildCopySecretPage(user: FullUser, client: OAuthClientWithSecret) {
    const { signInWithGrist: { issuer } = {} } = getGristConfig() as LoginPageConfig;
    if (!issuer) {
      throw new Error("No issuer configured for Sign in with getgrist.com");
    }

    const secret = btoa(
      JSON.stringify({
        oidcClientId: client.id,
        oidcClientSecret: client.secret,
        oidcIssuer: issuer,
        oidcSkipEndSessionEndpoint: true,
        owner: pick(user, "name", "email"),
      }),
    );

    return [
      loginCss.flexJustifyCenter(loginCss.gristLogo()),
      loginCss.centeredFormHeading("Configure your Grist server", testId("heading")),
      loginCss.centeredFormSubheading(
        "to use ",
        loginCss.lightlyBolded("getgrist.com authentication"),
        buildOAuthClientInfo({ registeredBy: user.email, ...client }),
      ),
      cssCopySecretBody(
        cssCopySecretParagraph(
          inlineMarkdown(
            "Almost done! Copy the secret below and paste it in the **Admin Panel** of your " +
            "Grist server to finish setting up **getgrist.com authentication**.",
          ),
        ),
        cssSecretContainer(
          cssSecretHeader(
            cssCopySecretButton(
              icon("Copy"),
              "Copy secret",
              dom.on("click", async (_ev, el) => {
                await copyToClipboard(secret);
                showTransientTooltip(
                  el,
                  "Secret copied to clipboard",
                  { key: "copy-secret" },
                );
              }),
            ),
          ),
          cssSecretBody(secret, testId("secret")),
        ),
        cssReturnToAdminPanelButton(
          "Return to Admin Panel",
          { href: new URL("/admin", client.uri).href, target: "_blank" },
          testId("return-to-admin-panel"),
        ),
        loginCss.centeredText(
          cssLink("Learn more about getgrist.com authentication", {
            href: commonUrls.help,
            target: "_blank",
          }),
        ),
      ),
    ];
  }

  /**
   * Fetches the active user in the current Grist session.
   */
  private async _fetchUser() {
    const { user } = await this._userAPI.getSessionActive();
    this._user.set(user);
  }
}

/**
 * The Sign in with getgrist.com select account page (`/oauth/select_account`).
 *
 * This is similar to the page at `/welcome/select-account`; the main differences with that
 * page and this one are styling and copy. While we could use that page instead of this
 * one for the getgrist.com authentication setup flow, its styling doesn't quite match the
 * rest of the pages in the flow, and it doesn't render correctly on mobile either.
 *
 * TODO: Port the styling and other improvements to `/welcome/select-account` and reuse it
 * for the getgrist.com authentication setup flow.
 */
export class SelectAccountPage extends Disposable {
  private readonly _api = new UserAPIImpl(getHomeUrl());
  private readonly _users = Observable.create<FullUser[] | null>(this, null);

  constructor() {
    super();

    this._fetchUsers().catch(reportError);
  }

  public buildDom() {
    return dom.maybe(this._users, users => loginCss.pageContainer(
      loginCss.flexJustifyCenter(
        loginCss.formContainer(
          loginCss.flexJustifyCenter(loginCss.gristLogo()),
          loginCss.centeredFormHeading("Select a Grist account", testId("heading")),
          loginCss.centeredFormSubheading(
            "to continue setting up ",
            loginCss.lightlyBolded("getgrist.com authentication"),
          ),
          dom("div",
            oauthCss.selectAccountForms(
              this._buildSelectSessionUserForm(users),
              this._buildLoginForm(),
            ),
            loginCss.centeredText(
              cssLink("Learn more about getgrist.com authentication", {
                href: commonUrls.help,
                target: "_blank",
              }),
            ),
          ),
        ),
      ),
    ));
  }

  /**
   * Builds a form to select an existing user from the Grist session.
   */
  private _buildSelectSessionUserForm(users: FullUser[]) {
    return oauthCss.selectAccountForm(
      oauthCss.accountButtons(
        users.map(user =>
          oauthCss.accountButton(
            oauthCss.account(
              createUserImage(user, "large"),
              oauthCss.accountNameAndEmail(
                cssMemberPrimary(user.name || dom("span", user.email), testId("select-account-user-name")),
                user.name ? cssMemberSecondary(user.email, testId("select-account-user-email")) : null,
              ),
            ),
            {
              type: "submit",
              name: "email",
              value: user.email,
            },
            testId("select-account-user"),
          ),
        ),
      ),
      handleSubmit({
        onSubmit: async (_fields, _form, event) =>
          this._api.setSessionActive((event.submitter as HTMLButtonElement).value),
        onSuccess: () => window.location.assign(buildURL("/oauth/register").href),
      }),
    );
  }

  /**
   * Builds a form that redirects to the login page (`/login`) to sign in to a new account.
   */
  private _buildLoginForm() {
    return oauthCss.selectAccountForm(
      oauthCss.accountButtons(
        oauthCss.accountButton(
          oauthCss.accountButton.cls("-add"),
          oauthCss.account(
            createUserImage("addUser", "large"),
            oauthCss.accountNameAndEmail(cssMemberPrimary("Add account")),
          ),
          { type: "submit" },
          testId("select-account-add-user"),
        ),
      ),
      handleSubmit({
        onSubmit: () => {
          const url = buildURL("/login", {
            searchParams: new URLSearchParams({
              next: "/oauth/register",
            }),
          });
          window.location.assign(url.href);
        },
      }),
    );
  }

  /**
   * Fetches the users in the current Grist session.
   */
  private async _fetchUsers() {
    const { users } = await this._api.getSessionAll();
    this._users.set(users);
  }
}

const cssForm = styled("form", `
  width: 100%;
`);

const cssFormField = styled("div", `
  margin-bottom: 16px;
`);

const cssFormFieldHint = styled(loginCss.lightText, `
  margin-top: 8px;
`);

const cssFormButtons = styled("div", `
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  margin-top: 32px;
`);

const cssPrimaryButton = styled(loginCss.bigPrimaryButton, `
  flex-grow: 1;
  text-align: center;
`);

const cssBasicButtonLink = styled(loginCss.bigBasicButtonLink, `
  flex-grow: 1;
  text-align: center;
`);

const cssCopySecretBody = styled("div", `
  width: 100%;
`);

const cssCopySecretParagraph = styled(loginCss.text, `
  margin-bottom: 24px;
`);

const cssSecretContainer = styled("div", `
  background: ${tokens.bgSecondary};
  border: 1px solid ${tokens.decoration};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`);

const cssSecretHeader = styled("div", `
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
`);

const cssSecretBody = styled("div", `
  color: ${tokens.body};
  flex-grow: 1;
  overflow-wrap: anywhere;
`);

const cssCopySecretButton = styled(textButton, `
  align-items: center;
  display: flex;
  gap: 8px;
`);

const cssReturnToAdminPanelButton = styled(loginCss.bigPrimaryButtonLink, `
  margin-bottom: 24px;
  margin-top: 24px;
  text-align: center;
  width: 100%;
`);
